import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, Users, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function MyTeam() {
  const qc = useQueryClient();
  const { data: team, isLoading } = useQuery({ queryKey: ["myTeam"], queryFn: async () => (await api.get("/my/team")).data });
  const { data: leaveReqs } = useQuery({ queryKey: ["myTeamLeave"], queryFn: async () => (await api.get("/my/team/leave-requests", { params: { status: "Pending_RO" } })).data });
  const { data: grievances } = useQuery({ queryKey: ["myTeamGrievances"], queryFn: async () => (await api.get("/my/team/grievances", { params: { status: "Pending_RO" } })).data });
  const { data: odReqs } = useQuery({ queryKey: ["myTeamOnDuty"], queryFn: async () => (await api.get("/my/team/on-duty", { params: { status: "Pending_RO" } })).data });
  const { data: compOffReqs } = useQuery({ queryKey: ["myTeamCompOff"], queryFn: async () => (await api.get("/my/team/comp-off-requests", { params: { status: "Pending_RO" } })).data });

  const decideLeave = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/leave-requests/${id}/ro-decide`, { action, remarks: action === "approve" ? "Recommended" : "Not recommended" })).data,
    onSuccess: () => { toast.success("Recommendation recorded"); qc.invalidateQueries({ queryKey: ["myTeamLeave"] }); qc.invalidateQueries({ queryKey: ["myTeam"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const decideGrievance = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/grievances/${id}/ro-decide`, { action, remarks: action === "approve" ? "Recommended" : "Not recommended" })).data,
    onSuccess: () => { toast.success("Recommendation recorded"); qc.invalidateQueries({ queryKey: ["myTeamGrievances"] }); qc.invalidateQueries({ queryKey: ["myTeam"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const decideOnDuty = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/on-duty/${id}/ro-decide`, { action, remarks: action === "approve" ? "Recommended" : "Not recommended" })).data,
    onSuccess: () => { toast.success("Recommendation recorded"); qc.invalidateQueries({ queryKey: ["myTeamOnDuty"] }); qc.invalidateQueries({ queryKey: ["myTeam"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const decideCompOff = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/my/team/comp-off-requests/${id}/decide`, { action, remarks: action === "approve" ? "Approved" : "Rejected" })).data,
    onSuccess: () => { toast.success("Decision recorded"); qc.invalidateQueries({ queryKey: ["myTeamCompOff"] }); qc.invalidateQueries({ queryKey: ["myTeam"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (isLoading || !team) return <Loading />;

  return (
    <div>
      <PageHeader overline="Reporting Officer" title="My Team" icon={Users}
        subtitle="Review your team's leave, On Duty, comp-off, and grievance requests. Leave recommendations go to HR for final approval; comp-off you decide outright." />

      <div className="grid sm:grid-cols-5 gap-4 mb-6 stagger">
        <Card className="p-5 border-slate-200 shadow-none">
          <span className="overline text-teal">Direct Reports</span>
          <p className="font-serif-display text-4xl text-navy mt-2">{team.team.length}</p>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <span className="overline text-teal">Leave Awaiting You</span>
          <p className="font-serif-display text-4xl text-navy mt-2">{team.pending_leave}</p>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <span className="overline text-teal">On Duty Awaiting You</span>
          <p className="font-serif-display text-4xl text-navy mt-2">{team.pending_on_duty || 0}</p>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <span className="overline text-teal">Comp-Off Awaiting You</span>
          <p className="font-serif-display text-4xl text-navy mt-2">{team.pending_comp_off}</p>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <span className="overline text-teal">Grievances Awaiting You</span>
          <p className="font-serif-display text-4xl text-navy mt-2">{team.pending_grievance}</p>
        </Card>
      </div>

      <Tabs defaultValue="leave">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="leave" data-testid="tab-team-leave">Leave Requests ({leaveReqs?.length || 0})</TabsTrigger>
          <TabsTrigger value="on-duty" data-testid="tab-team-on-duty">On Duty ({odReqs?.length || 0})</TabsTrigger>
          <TabsTrigger value="comp-off" data-testid="tab-team-comp-off">Comp-Off Requests ({compOffReqs?.length || 0})</TabsTrigger>
          <TabsTrigger value="grievances" data-testid="tab-team-grievances">Grievances ({grievances?.length || 0})</TabsTrigger>
          <TabsTrigger value="roster" data-testid="tab-team-roster">Team Roster ({team.team.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="leave" className="mt-6">
          {(leaveReqs || []).length === 0 ? (
            <EmptyState icon={Users} title="No leave requests awaiting your review" />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Type</TableHead>
                  <TableHead className="overline text-slate-500">From → To</TableHead>
                  <TableHead className="overline text-slate-500">Days</TableHead>
                  <TableHead className="overline text-slate-500">Reason</TableHead>
                  <TableHead className="text-right overline text-slate-500">Recommend</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {leaveReqs.map((r) => (
                    <TableRow key={r.id} className="table-row-i" data-testid={`team-leave-${r.id}`}>
                      <TableCell className="font-medium text-navy">{r.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{r.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600">{r.leave_type_code}</TableCell>
                      <TableCell className="text-slate-600">{r.from_date} → {r.to_date}</TableCell>
                      <TableCell className="text-slate-600">{r.days}{r.half_day ? " (½)" : ""}</TableCell>
                      <TableCell className="text-slate-500 text-xs min-w-[200px] max-w-[340px] whitespace-normal break-words">{r.reason}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => decideLeave.mutate({ id: r.id, action: "approve" })} data-testid={`team-leave-approve-${r.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => decideLeave.mutate({ id: r.id, action: "reject" })} data-testid={`team-leave-reject-${r.id}`}><XCircle className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="on-duty" className="mt-6">
          {(odReqs || []).length === 0 ? (
            <EmptyState icon={Users} title="No On Duty requests awaiting your review" />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Type</TableHead>
                  <TableHead className="overline text-slate-500">Dates</TableHead>
                  <TableHead className="overline text-slate-500">Place & Purpose</TableHead>
                  <TableHead className="text-right overline text-slate-500">Recommend</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {odReqs.map((o) => (
                    <TableRow key={o.id} className="table-row-i" data-testid={`team-od-${o.id}`}>
                      <TableCell className="font-medium text-navy">{o.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{o.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600">{o.od_type}</TableCell>
                      <TableCell className="text-slate-600 text-sm whitespace-nowrap">{o.from_date === o.to_date ? o.from_date : `${o.from_date} → ${o.to_date}`}<span className="block text-xs text-slate-400">{o.half_day ? "Half day" : `${o.days} day(s)`}</span></TableCell>
                      <TableCell className="text-slate-600 text-sm min-w-[200px] max-w-[340px] whitespace-normal break-words"><span className="font-medium text-navy block">{o.place}</span><span className="text-xs text-slate-500">{o.purpose}</span></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => decideOnDuty.mutate({ id: o.id, action: "approve" })}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => decideOnDuty.mutate({ id: o.id, action: "reject" })}><XCircle className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="comp-off" className="mt-6">
          {(compOffReqs || []).length === 0 ? (
            <EmptyState icon={Users} title="No comp-off requests awaiting your review" />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Date</TableHead>
                  <TableHead className="overline text-slate-500">Hours</TableHead>
                  <TableHead className="overline text-slate-500">Reason</TableHead>
                  <TableHead className="text-right overline text-slate-500">Decide</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {compOffReqs.map((c) => (
                    <TableRow key={c.id} className="table-row-i" data-testid={`team-comp-off-${c.id}`}>
                      <TableCell className="font-medium text-navy">{c.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{c.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600 font-mono-i text-sm">{c.work_date}</TableCell>
                      <TableCell className="text-slate-600">{c.source === "work_request" ? <span className="text-xs">Advance request<span className="block text-[10px] text-slate-400">approve = permission to work; credited after 6+ hours</span></span> : `${c.hours_worked}h`}</TableCell>
                      <TableCell className="text-slate-500 text-xs min-w-[200px] max-w-[340px] whitespace-normal break-words">{c.reason}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => decideCompOff.mutate({ id: c.id, action: "approve" })} data-testid={`team-comp-off-approve-${c.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => decideCompOff.mutate({ id: c.id, action: "reject" })} data-testid={`team-comp-off-reject-${c.id}`}><XCircle className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="grievances" className="mt-6">
          {(grievances || []).length === 0 ? (
            <EmptyState icon={Users} title="No grievances awaiting your review" />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Category</TableHead>
                  <TableHead className="overline text-slate-500">Subject</TableHead>
                  <TableHead className="overline text-slate-500">Priority</TableHead>
                  <TableHead className="text-right overline text-slate-500">Recommend</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {grievances.map((g) => (
                    <TableRow key={g.id} className="table-row-i" data-testid={`team-grievance-${g.id}`}>
                      <TableCell className="font-medium text-navy">{g.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{g.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600">{g.category}</TableCell>
                      <TableCell className="text-slate-600 text-sm min-w-[220px] max-w-[360px]"><span className="font-medium text-navy block">{g.subject}</span><span className="text-xs text-slate-500 whitespace-pre-line break-words block mt-0.5">{g.description}</span></TableCell>
                      <TableCell className="text-slate-600">{g.priority}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => decideGrievance.mutate({ id: g.id, action: "approve" })} data-testid={`team-grievance-approve-${g.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => decideGrievance.mutate({ id: g.id, action: "reject" })} data-testid={`team-grievance-reject-${g.id}`}><XCircle className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="roster" className="mt-6">
          {team.team.length === 0 ? (
            <EmptyState icon={Users} title="No direct reports" />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Name</TableHead>
                  <TableHead className="overline text-slate-500">Employee ID</TableHead>
                  <TableHead className="overline text-slate-500">Designation</TableHead>
                  <TableHead className="overline text-slate-500">Department</TableHead>
                  <TableHead className="overline text-slate-500">Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {team.team.map((e) => (
                    <TableRow key={e.id} className="table-row-i" data-testid={`team-member-${e.id}`}>
                      <TableCell className="font-medium text-navy">{e.identity?.name}</TableCell>
                      <TableCell className="font-mono-i text-sm text-slate-500">{e.employee_id}</TableCell>
                      <TableCell className="text-slate-600">{e.designation_title || "—"}</TableCell>
                      <TableCell className="text-slate-600">{e.department_name || "—"}</TableCell>
                      <TableCell><StatusBadge status={e.employment?.employment_status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
